import './style.css';
import { CalculatorForm, SavingsGrowthGraphCalculatorFormProps } from './components/calculator-form';
import { BlockBase } from '@/queries/page';
import { Background, LabelsType } from './types';
import { renderBackground } from './components/background';
import { Headline, HeadlineProps } from '../../typography/headline/headline';
import { ParagraphProps } from '../../typography/paragraph/paragraph';
import { useSavingsGrowthGraphCalculator } from './hooks/use-savings-growth-graph-calculator';
import { Button } from '../../typography/button/button';
import { SavingsGrowthGraph } from './components/graph';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Table } from './components/table';

type HeadingBlock = BlockBase & HeadlineProps;
type ParagraphBlock = BlockBase & ParagraphProps;
type CalculatorFormBlock = BlockBase & {
    blockName: 'resurs/savings-growth-graph-calculator-form';
    settings: SavingsGrowthGraphCalculatorFormProps['settings'];
    CTAbutton: SavingsGrowthGraphCalculatorFormProps['CTAbutton'];
};

export type InnerBlock = HeadingBlock | ParagraphBlock | CalculatorFormBlock;

export type SavingsGrowthGraphCalculatorProps = {
    labels: LabelsType;
    background: Background;
    descriptionText: string;
    innerBlocks: InnerBlock[];
    hiddenProducts?: string[];
    productOrder?: string[];
    showCustomInterestRateTextField: boolean;
    showRelativeNumberOfYears?: boolean;
};

const isBlockWithAttrs = <T extends InnerBlock>(block: InnerBlock, name: T['blockName']): block is T => block.blockName === name;

export const SavingsGrowthGraphCalculator = (props: SavingsGrowthGraphCalculatorProps) => {
    const {
        labels,
        background,
        innerBlocks,
        hiddenProducts = [],
        productOrder = [],
        showCustomInterestRateTextField = false,
        showRelativeNumberOfYears = false,
    } = props;
    const [heading, subHeading, calculatorForm, descriptionText] = innerBlocks;

    // Filter out hidden products
    const getVisibleProducts = (allProducts: typeof products) => allProducts?.filter(product => !hiddenProducts.includes(product.code)) || [];

    // Sort products by productOrder
    const getSortedProducts = (visibleProducts: NonNullable<typeof products>) => {
        if (!productOrder.length || !visibleProducts.length) return visibleProducts;
        return [...visibleProducts].sort((a, b) => {
            const indexA = productOrder.indexOf(a.code);
            const indexB = productOrder.indexOf(b.code);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    };

    const {
        products,
        chosenProduct,
        setChosenProduct,
        savingsCalculatorState,
        updateSavingsCalculatorState,
        futureTotalValue,
        totalInterestEarned,
        graphData,
        formatNumber,
        getLabelCallback,
    } = useSavingsGrowthGraphCalculator({
        initialValues: isBlockWithAttrs<CalculatorFormBlock>(calculatorForm, 'resurs/savings-growth-graph-calculator-form')
            ? {
                  startAmount: calculatorForm.settings.defaultStartAmount,
                  monthlyDeposit: calculatorForm.settings.defaultMonthlyAmount,
                  years: calculatorForm.settings.defaultYears,
                  annualInterestRate: null,
                  customInterestRate: '',
              }
            : null,
        labels,
        hiddenProducts,
        productOrder,
    });

    const [CURRENT_YEAR] = useState(() => new Date().getFullYear());
    const [showingTable, setShowingTable] = useState(false);

    // Get sorted visible products for display
    const sortedProducts = useMemo(() => {
        const visible = getVisibleProducts(products);
        return getSortedProducts(visible);
    }, [products, hiddenProducts, productOrder]);

    const calculatorFormProps = isBlockWithAttrs<CalculatorFormBlock>(calculatorForm, 'resurs/savings-growth-graph-calculator-form')
        ? calculatorForm
        : null;

    return (
        <div data-rico="suave" data-component="savings-growth-graph-calculator" className="savings-graph-calculator r-relative r-w-full">
            {background?.colorMobile?.color ? (
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                            @media (max-width: 820px) { .savings-graph-calculator { background-color: ${background?.colorMobile?.color} } }
                        `,
                    }}
                />
            ) : null}
            <div className="r-hidden md:r-block">{renderBackground(background)}</div>
            <div className="r-container r-relative r-z-20 r-px-4 r-py-6 md:r-px-8">
                <div className="r-grid r-gap-8 md:r-grid-cols-2">
                    <div className="md:r-col-span-1">
                        {isBlockWithAttrs<HeadingBlock>(heading, 'resurs/heading') && (
                            <Headline {...heading} margin="bottom">
                                {heading.children}
                            </Headline>
                        )}

                        {isBlockWithAttrs<ParagraphBlock>(subHeading, 'resurs/paragraph') && (
                            <p className="r-text-lg r-text-neutral-900" dangerouslySetInnerHTML={{ __html: subHeading.children }} />
                        )}
                    </div>

                    <div className="r-flex r-min-w-0 r-flex-col r-gap-2 md:r-col-start-1 md:r-gap-4">
                        {calculatorFormProps && (
                            <CalculatorForm
                                products={sortedProducts}
                                formatNumber={formatNumber}
                                getLabel={getLabelCallback}
                                chosenProduct={chosenProduct}
                                setChosenProduct={setChosenProduct}
                                settings={calculatorFormProps.settings}
                                CTAbutton={calculatorFormProps.CTAbutton}
                                savingsCalculatorState={savingsCalculatorState}
                                updateSavingsCalculatorState={updateSavingsCalculatorState}
                                showCustomInterestRateTextField={showCustomInterestRateTextField}
                            />
                        )}

                        <div className="r-flex r-flex-col r-justify-between r-gap-8 md:r-h-[5.25rem] md:r-flex-row md:r-gap-4">
                            <div className="r-flex-1">
                                {futureTotalValue > 0 && savingsCalculatorState.years && totalInterestEarned > 0 && (
                                    <p
                                        className="summaryText r-text-lg"
                                        dangerouslySetInnerHTML={{
                                            __html: getLabelCallback('summaryCalculationText', {
                                                years: savingsCalculatorState.years,
                                                total: formatNumber(Math.round(futureTotalValue)),
                                                interest: formatNumber(Math.round(totalInterestEarned)),
                                            }),
                                        }}
                                    />
                                )}
                            </div>
                            {calculatorFormProps && (
                                <Button
                                    className={clsx('!r-mr-0 r-max-h-[3.125rem] r-shrink-0 max-md:r-w-full', {
                                        'r-pointer-events-none': !chosenProduct?.code && !savingsCalculatorState.customInterestRate,
                                    })}
                                    target={calculatorFormProps.CTAbutton.target}
                                    to={
                                        calculatorFormProps.CTAbutton.appendProductCode && chosenProduct?.code
                                            ? `${calculatorFormProps.CTAbutton.url}?productCode=${chosenProduct.code}`
                                            : calculatorFormProps.CTAbutton.url
                                    }
                                    disabled={!chosenProduct?.code && !savingsCalculatorState.customInterestRate}
                                >
                                    {calculatorFormProps.CTAbutton.title}
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Mobile */}
                    <details className="r-rounded-2xl r-border r-border-solid r-border-neutral-400 md:r-hidden">
                        <summary className="r-flex r-flex-row r-justify-between r-p-4">
                            <h3 className="r-text-xl r-font-medium">{getLabelCallback('savingsGrowthOverTime')}</h3>
                            <svg
                                className="caret r-h-7 r-transition-transform r-duration-300 r-ease-in-out"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M4.18179 8.81819C4.00605 8.64245 4.00605 8.35753 4.18179 8.18179L7.18179 5.18179C7.26618 5.0974 7.38064 5.04999 7.49999 5.04999C7.61933 5.04999 7.73379 5.0974 7.81819 5.18179L10.8182 8.18179C10.9939 8.35753 10.9939 8.64245 10.8182 8.81819C10.6424 8.99392 10.3575 8.99392 10.1818 8.81819L7.49999 6.13638L4.81819 8.81819C4.64245 8.99392 4.35753 8.99392 4.18179 8.81819Z"
                                    fill="#000"
                                />
                            </svg>
                        </summary>
                        <div className="r-p-4">
                            {showingTable ? (
                                <Table
                                    currentYear={CURRENT_YEAR}
                                    graphData={graphData}
                                    getLabel={getLabelCallback}
                                    formatNumber={formatNumber}
                                    showRelativeNumberOfYears={showRelativeNumberOfYears}
                                />
                            ) : (
                                <SavingsGrowthGraph
                                    currentYear={CURRENT_YEAR}
                                    graphData={graphData}
                                    years={savingsCalculatorState.years || 15}
                                    getLabel={getLabelCallback}
                                    showRelativeNumberOfYears={showRelativeNumberOfYears}
                                />
                            )}
                        </div>
                        <div className="r-flex r-w-full r-justify-end r-p-4">
                            <button
                                className={clsx(
                                    'r-rounded-3xl r-border-2 r-border-black r-px-6 r-py-1 r-text-sm r-font-bold r-text-black r-transition-all hover:r-bg-tertiary-500 hover:r-text-white focus-visible:r-bg-tertiary-500 focus-visible:r-text-white md:r-px-8 md:r-py-2 md:r-text-base',
                                    {
                                        isActive: 'r-bg-tertiary-500 r-text-white',
                                    }
                                )}
                                onClick={() => setShowingTable(!showingTable)}
                            >
                                {showingTable ? getLabelCallback('showGraphButton') : getLabelCallback('showTableButton')}
                            </button>
                        </div>
                    </details>
                    {/* Desktop */}
                    <div className="r-hidden r-flex-col r-items-center r-justify-center md:r-flex md:r-pl-16">
                        <div className="r-flex r-h-[28rem] r-w-full r-flex-col r-rounded-2xl r-bg-white r-px-4 r-py-6 r-shadow-lg md:r-px-8">
                            <h3 className="r-mb-6 r-mt-2 r-text-3xl r-font-medium md:r-mb-4 md:r-mt-0">
                                {getLabelCallback('savingsGrowthOverTime')}
                            </h3>
                            {showingTable ? (
                                <Table
                                    currentYear={CURRENT_YEAR}
                                    graphData={graphData}
                                    getLabel={getLabelCallback}
                                    formatNumber={formatNumber}
                                    showRelativeNumberOfYears={showRelativeNumberOfYears}
                                />
                            ) : (
                                <SavingsGrowthGraph
                                    currentYear={CURRENT_YEAR}
                                    graphData={graphData}
                                    years={savingsCalculatorState.years || 15}
                                    getLabel={getLabelCallback}
                                    showRelativeNumberOfYears={showRelativeNumberOfYears}
                                />
                            )}
                            <div className="r-mt-auto r-flex r-w-full r-justify-end">
                                <button
                                    className={clsx(
                                        'r-rounded-3xl r-border-2 r-border-black r-px-6 r-py-1 r-text-sm r-font-bold r-text-black r-transition-all hover:r-bg-tertiary-500 hover:r-text-white focus-visible:r-bg-tertiary-500 focus-visible:r-text-white md:r-px-8 md:r-py-2 md:r-text-base',
                                        {
                                            isActive: 'r-bg-tertiary-500 r-text-white',
                                        }
                                    )}
                                    onClick={() => setShowingTable(!showingTable)}
                                >
                                    {showingTable ? getLabelCallback('showGraphButton') : getLabelCallback('showTableButton')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        {isBlockWithAttrs<ParagraphBlock>(descriptionText, 'resurs/paragraph') && (
                            <p className="r-text-sm r-text-zinc-500" dangerouslySetInnerHTML={{ __html: descriptionText.children }} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
